import React from 'react';
import type {Collection} from '@shopify/hydrogen/storefront-api-types';
import {useLoaderData, useParams, useLocation} from '@remix-run/react';
import {gsap} from 'gsap';
import {ScrollTrigger} from 'gsap/dist/ScrollTrigger';
import {useIsHydrated} from '~/hooks/useIsHydrated';

//domains
import {loader} from '~/routes/($lang)/search';

//components
import CollectionBreadcrumb from '~/components/collection/CollectionBreadcrumb';
import CollectionSorting from 'components/collection/CollectionSorting';
import CollectionProducts from 'components/collection/CollectionProducts';
import CollectionPagination from 'components/collection/CollectionPagination';
import NativeCollectionFilter from '~/components/collection/CollectionFilter/NativeCollectionFilter';
import CollectionMobileFilterAndSort from '../collection/CollectionMobileFilterAndSort';

//domains
import {MODEL_FACET_ID} from '~/lib/brandModels';

// Search spans every brand, so a single brand's 型號 list has no business
// leading the sidebar here — same reasoning as CollectionMain's gate.
const SEARCH_HIDDEN_FACETS = [MODEL_FACET_ID];

export default function SearchMain() {
  const {totalProducts, translation} = useLoaderData<typeof loader>();
  const filterColRef = React.useRef<HTMLDivElement>(null);
  const mainRef = React.useRef<HTMLDivElement>(null);
  const sortingRef = React.useRef<HTMLDivElement>(null);

  const isHydrated = useIsHydrated();

  const {search} = useLocation();
  const searchParams = new URLSearchParams(search);
  const currentSearchQuery = searchParams.get('q');

  return (
    <div className="grid lg:grid-cols-8 lg:gap-10 lg:pt-8" ref={mainRef}>
      <div className="relative lg:col-span-2 hidden lg:block">
        <div className="relative lg:col-span-2">
          <div className="" ref={filterColRef}>
            <div className="lg:pr-4 lg:pb-64">
              <div className="hidden lg:block">
                <NativeCollectionFilter hideFacets={SEARCH_HIDDEN_FACETS} />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="lg:col-span-6 pt-28 lg:pt-0">
        {currentSearchQuery && (
          <div className="mb-4">
            <h2 className="uppercase font-bold text-xl tracking-wide">
              Search Results for {`"${currentSearchQuery}"`}
            </h2>
          </div>
        )}
        <div
          ref={sortingRef}
          className="fixed left-1/2 top-0 lg:top-auto  md:mt-4 flex flex-col md:flex-row w-screen -translate-x-1/2 translate-y-[130px] lg:translate-y-0 z-[30] lg:z-[11] items-center justify-between bg-white px-5 mt-0 pt-1 pb-1 md:py-2 lg:relative lg:left-0 lg:mt-0 lg:mb-14 lg:h-14 lg:w-full lg:translate-x-0 lg:bg-hk-orange lg:px-4 search-collections-bar"
        >
          <div className="text-sm w-full lg:w-auto">
            {translation.product_listing ?? `Product Listing`}: {totalProducts}
          </div>
          <div className="hidden lg:block">
            <CollectionSorting variant="search" />
          </div>
        </div>
        <div className="fixed left-1/2 z-[30] mt-[39px] w-screen -translate-x-1/2 px-5 lg:hidden top-0 lg:top-auto translate-y-[114px] lg:translate-y-0 search-collections-filter-sort">
          {isHydrated && (
            <CollectionMobileFilterAndSort
              sortVariant="search"
              hideFacets={SEARCH_HIDDEN_FACETS}
            />
          )}
        </div>
        <CollectionProducts />
        <CollectionPagination />
      </div>
    </div>
  );
}
